import React from 'react';
import { motion } from 'framer-motion';
import { CodeIcon, TerminalIcon } from 'lucide-react';
export function AboutSection() {
  return <section id="about" className="py-20 sm:py-28 px-4 bg-slate-900/50" aria-labelledby="about-heading" data-id="element-9">
      <div className="max-w-6xl mx-auto" data-id="element-10">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5
      }} className="text-center mb-12" data-id="element-11">
          <h2 id="about-heading" className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-12">
            About Me
          </h2>
          <div className="w-20 h-1 bg-emerald-500 mx-auto rounded-full" aria-hidden="true" data-id="element-13" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-14">
          {/* Avatar */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="flex justify-center lg:justify-end" data-id="element-15">
            <div className="relative" data-id="element-16">
              <div className="w-64 h-64 sm:w-80 sm:h-80 rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 flex items-center justify-center" data-id="element-17">
                <img src="https://qxdzurhfpysmxwcezdwv.supabase.co/storage/v1/object/sign/chore/E8578518-920E-4633-A1B7-035980C85E22.JPG?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV82YWRlNTllNi0zZGNkLTQzMWYtYmI4MC1kODNjNzJmODc2NjUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjaG9yZS9FODU3ODUxOC05MjBFLTQ2MzMtQTFCNy0wMzU5ODBDODVFMjIuSlBHIiwiaWF0IjoxNzcyNjk4OTEzLCJleHAiOjE4MDQyMzQ5MTN9.gUDA91R7OOZoeer2dScmxysXWCs1u0IpvcjZbFgrMOw" className="w-full h-full object-cover object-[center_-25px]" alt="" data-id="element-18" />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-500/20 rounded-xl -z-10" aria-hidden="true" data-id="element-19" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-emerald-500/20 rounded-xl -z-10" aria-hidden="true" data-id="element-20" />

              {/* Floating badge */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4,
              duration: 0.3
            }} className="absolute -bottom-6 -right-6 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 shadow-xl" data-id="element-21">
                <div className="flex items-center gap-2" data-id="element-22">
                  <TerminalIcon className="w-5 h-5 text-emerald-400" aria-hidden="true" data-id="element-23" />
                  <span className="font-mono text-sm text-emerald-400" data-id="element-24">
                    Open to work
                  </span>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} data-id="element-25">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-26">Peter Pham</h3>
            <p className="text-emerald-400 font-medium mb-6" data-id="element-27">
              Junior Web Developer
            </p>

            <div className="space-y-4 text-slate-300 leading-relaxed mb-8" data-id="element-28">
              <p data-id="element-29">
                I'm a passionate developer who loves building tools that make
                other developers' lives easier. My journey started with
                curiosity about how websites work, and now I spend my days
                crafting clean, efficient code and exploring new technologies.
              </p>
              <p data-id="element-30">
                When I'm not coding, you'll find me contributing to open-source
                projects, writing technical blog posts, or experimenting with
                new frameworks. I believe in writing code that's not just
                functional, but also maintainable and elegant.
              </p>
            </div>

            {/* Tech Stack */}
            <div data-id="element-31">
              <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-32">
                <CodeIcon className="w-4 h-4" aria-hidden="true" data-id="element-33" />
                Tech Stack
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-34">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300" data-id="element-35">
                  <span className="w-2 h-2 rounded-full bg-blue-400" aria-hidden="true" data-id="element-36" />
                  React
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300" data-id="element-37">
                  <span className="w-2 h-2 rounded-full bg-blue-500" aria-hidden="true" data-id="element-38" />
                  TypeScript
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300" data-id="element-39">
                  <span className="w-2 h-2 rounded-full bg-green-500" aria-hidden="true" data-id="element-40" />
                  Node.js
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300" data-id="element-41">
                  <span className="w-2 h-2 rounded-full bg-yellow-400" aria-hidden="true" data-id="element-42" />
                  Python
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300" data-id="element-43">
                  <span className="w-2 h-2 rounded-full bg-cyan-400" aria-hidden="true" data-id="element-44" />
                  Tailwind CSS
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300" data-id="element-45">
                  <span className="w-2 h-2 rounded-full bg-orange-500" aria-hidden="true" data-id="element-46" />
                  Git
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300" data-id="element-47">
                  <span className="w-2 h-2 rounded-full bg-green-400" aria-hidden="true" data-id="element-48" />
                  MongoDB
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300" data-id="element-49">
                  <span className="w-2 h-2 rounded-full bg-sky-400" aria-hidden="true" data-id="element-50" />
                  PostgreSQL
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}